import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";

export default function Plans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [loading, setLoading] = useState("");

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);

  const subscribe = async (plan) => {
    if (!user) return navigate("/login");
    if (plan.key === "free") return toast.info("You're on the free plan by default.");
    setLoading(plan.key);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan_key: plan.key, interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setLoading(""); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-5xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-black font-display">Choose your plan</h1>
        <p className="text-slate-400 mt-2">Listening is always free. Upgrade to create AI music & unlock Voice Studio.</p>
        <div className="inline-flex items-center gap-1 bg-white/5 rounded-full p-1 mt-4 border border-white/10">
          {["monthly", "annual"].map((i) => (
            <button key={i} onClick={() => setInterval(i)} data-testid={`interval-${i}`} className={`px-4 py-1.5 rounded-full text-sm capitalize ${interval === i ? "jx-cta text-white" : "text-slate-400"}`}>{i}{i === "annual" && <span className="text-[10px] ml-1 text-cyan-300">save ~17%</span>}</button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const price = interval === "annual" ? p.price_annual : p.price_monthly;
          const isCurrent = user?.plan_key === p.key;
          return (
            <div key={p.key} className={`jx-card rounded-2xl p-6 flex flex-col ${p.badge === "Best Value" ? "border-purple-500/50 jx-glow-purple" : ""}`} data-testid={`plan-${p.key}`}>
              {p.badge && <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 mb-1">{p.badge}</span>}
              <h3 className="text-lg font-bold font-display">{p.name}</h3>
              <p className="text-3xl font-black mt-2">${price}<span className="text-sm text-slate-500 font-normal">/{interval === "annual" ? "yr" : "mo"}</span></p>
              <ul className="text-xs text-slate-400 space-y-2 mt-4 flex-1">
                {p.features.map((f, i) => <li key={i} className="flex gap-2"><Check size={13} className="text-cyan-400 shrink-0 mt-0.5" />{f}</li>)}
              </ul>
              <button onClick={() => subscribe(p)} disabled={loading === p.key || isCurrent} data-testid={`subscribe-${p.key}`}
                className={`mt-5 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${isCurrent ? "bg-white/10 text-slate-400" : p.key === "free" ? "border border-white/15 text-white" : "jx-cta text-white"}`}>
                {loading === p.key ? <Loader2 className="animate-spin" size={16} /> : isCurrent ? "Current Plan" : p.key === "free" ? "Free" : <><Crown size={15} /> Subscribe</>}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-slate-500 mt-6">Secure recurring billing via Stripe. Test card: 4242 4242 4242 4242. Cancel anytime — your music stays.</p>
    </div>
  );
}
